import { PhpArray } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { buildExplorableEncounterLinks, type EncounterLinkDeps } from './encounter_link_and_ability_builders';
import { at, entries, type Row } from './support';
import { encounterField, computedValue, plTotal } from '../../../shared/array-types';
import { readItemField } from '../../compile/item-field-reader';
import { surfaceField } from '../../compile/from-base-encounter-media';
import { encounterZoneLabel, roomMapKeyLabel } from '../../compile/encounter-zone-label';
import { plTotalOf } from '../../../shared/pl-browser-pricing';
import type { TableCell } from '../../../catalog-list/types';
import { htmlspecialchars } from './support';
import { decodeHtmlEntitiesForPlainText } from '../../../shared/decode-html-entities';

export interface EncounterDisplayInput {
    encounters: Record<string, unknown>;
    names?: Record<string, string>;
    roster?: { encounter_creature_id?: string; item_id?: string; item_type?: string; pl: number | null }[];
}

export function gmNotesVisible(input: { can_manage?: boolean; notes?: unknown }): boolean {
    return input.can_manage === true && typeof input.notes === 'string' && input.notes !== '';
}

export function locationDisplayText(row: unknown, field: string): string {
    const value = surfaceField(row as Record<string, unknown>, field);
    return field === 'count_attached_encounters' && Number(value ?? 0) === 0 ? '' : decodeHtmlEntitiesForPlainText(value);
}

export function displayEncounterZone(row: EncounterDisplayInput): string {
    const computed = computedValue(row, 'encounter', 'encounter_zone');
    return decodeHtmlEntitiesForPlainText(computed ?? encounterZoneLabel(row));
}

export function displayEncounterRoomLabel(row: EncounterDisplayInput, mapKey: string, roomCount: number): string {
    const prefix = String(encounterField(row, 'encounter_zone_prefix') || encounterField(row, 'location_zone_prefix') || '');
    return String(mapKey ?? '').trim() ? roomMapKeyLabel(prefix, mapKey) : roomCount <= 1 ? displayEncounterZone(row).trim() : '';
}

export function encounterDisplayTotals(rows: NonNullable<EncounterDisplayInput['roster']>) {
    let stored = 0, pendingOther = 0;
    const pendingIds: string[] = [];
    for (const row of rows) {
        // A lair is an encounter controller, not a creature with a Power Level.
        if (readItemField(row, 'item_type') === 'lair') continue;
        const price = plTotal(row);
        if (price !== undefined && Number.isFinite(price)) stored += Math.trunc(price);
        else if (readItemField(row, 'item_type') === 'player_character' || !readItemField(row, 'item_id')) pendingOther++;
        else pendingIds.push(String(readItemField(row, 'item_id') ?? ''));
    }
    return { stored, pendingIds, pendingOther, count: rows.length };
}

/** Fill the existing catalog table model before its sort/filter indexes are built. */
export function encounterDisplayCells(input: EncounterDisplayInput): Record<string, TableCell> {
    const text = (value: string | number): TableCell => ({ segments: [{ kind: 'text', value: String(value) }], sort: value, text: String(value).toLowerCase() });
    const cells: Record<string, TableCell> = { zone: text(displayEncounterZone(input)) };
    for (const [cell, field] of [['environment', 'encounter_environment'], ['weather', 'encounter_weather'], ['light', 'encounter_light']] as const) {
        const id = encounterField<string>(input, field);
        cells[cell!] = { segments: id ? [{ kind: 'raw', html: `<span role="button" data-ft-encounter-info="${htmlspecialchars(id)}"></span>` }] : [], sort: '', text: '' };
    }
    if (input.roster) {
        const totals = encounterDisplayTotals(input.roster), total = plTotalOf(totals, new Map());
        cells.plt = total !== null ? text(total) : { segments: [], sort: 'pending', text: 'pending',
            pl: { stored: totals.stored, pending_ids: totals.pendingIds, pending_other: totals.pendingOther } };
    }
    return cells;
}

export interface LinksInput extends EncounterDisplayInput {
    can_manage: boolean;
    links: Row[];
    markers: Row[];
}
function plain(value: unknown): any {
    if (value instanceof PhpArray) return Object.fromEntries(entries(value).map(([key, child]) => [key, plain(child)]));
    return value;
}

export function renderEncounterDisplayLinks(input: LinksInput, ui: { icon: (id: string) => string; info: (id: unknown) => string; thumbnail: (data: unknown) => string }): string {
    const links = input.links.map(link => ({ ...plain(link), encounter_name: decodeHtmlEntitiesForPlainText(encounterField(plain(link), 'encounter_name')) }));
    const deps: EncounterLinkDeps = {
        encounter_field: (row, key) => encounterField(plain(row), key),
        layer_value: (row, key) => surfaceField(plain(row), key),
        layer_has: (row, key) => {
            const value = surfaceField(plain(row), key);
            // Raw joined rows retain zero flags, unlike the lean browser blobs.
            return key === 'encounter_link_private' || key === 'encounter_explored'
                ? Number(value ?? 0) !== 0 : value !== null && value !== undefined;
        },
        is_at_least_employee: () => input.can_manage,
        has_campaign_permission: () => input.can_manage,
        fetch_location_map_markers: () => input.markers,
        getCoordinates: value => {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value;
            return { lat: Number(at(parsed, 'lat') ?? 0), lng: Number(at(parsed, 'lng') ?? 0) };
        },
        explorable_encounter_links: () => links,
        icon_ph: name => ui.icon(name),
        open_icon_info: ui.info,
        ft_thumbnail_mount: row => ui.thumbnail({ item_id: at(row, 'encounter_id', 0), name: at(row, 'encounter_name', 0), w: 60, h: 60, link: true }),
    };
    const unavailable = (): never => { throw new Error('display-unexpected-rule-dependency'); };
    const env: PortEnv<EncounterLinkDeps> = { deps, globals: { user_id: 'viewer' }, functionStatics: {}, out: unavailable,
        rand: unavailable, now: unavailable, nowMicro: unavailable, uuid: unavailable, uniqid: unavailable,
        exit: unavailable, warn: unavailable, randomBytes: unavailable, gmdate: unavailable, hrtime: unavailable };
    return buildExplorableEncounterLinks({ campaigns: { campaign_created_by_id: input.can_manage ? 'viewer' : 'other' } }, input as unknown as Row, env);
}

